import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import {
    DOC_TYPES, INPUT_TYPES, InputDocType, L2_PROCESSES, Landscape, MissingDocument
} from '~/models/document-finder.models';
import { DocumentFinderService } from '~/services/document-finder.service';
import { DocumentFinderApiService } from '~/services/document-finder-api.service';
import { downloadBlob } from '~/utils/download';

export type MissingForm = Omit<MissingDocument, 'key'>;

/** Form for reporting (or editing) a document the agent missed. Rendered only while open. */
@Component({
    selector: 'df-missing-modal',
    templateUrl: './df-missing-modal.component.html',
    styleUrls: ['./df-missing-modal.component.scss'],
    standalone: false
})
export class DfMissingModalComponent implements OnInit {
    public readonly types = INPUT_TYPES;
    public readonly docTypes = DOC_TYPES as Record<string, (typeof DOC_TYPES)[keyof typeof DOC_TYPES]>;
    public readonly l2 = L2_PROCESSES;

    public form: MissingForm = { type: 'cdd', land: 'tobe', l2: 'LSC-210', title: '' };
    public index: number | null = null;
    public titleInvalid: boolean = false;
    public file: File | null = null;
    public get busy(): boolean { return this.df.missingUploadBusy; }
    public set busy(value: boolean) { this.df.missingUploadBusy = value; }
    public error = '';
    public fileInvalid = false;
    private uploadId = crypto.randomUUID();

    constructor(public df: DocumentFinderService, private api: DocumentFinderApiService) {}

    public selectFile(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.file = null;
        this.error = '';
        this.fileInvalid = false;
        const file = input.files?.[0];
        if (!file) { return; }
        if (!/\.(pdf|docx|xlsx|pptx|bpmn)$/i.test(file.name)) {
            this.error = 'Choose PDF, DOCX, XLSX, PPTX, or BPMN.';
        } else if (!file.size || file.size > 20 * 1024 * 1024) {
            this.error = 'Choose a non-empty file no larger than 20 MiB.';
        } else {
            this.file = file;
            this.uploadId = crypto.randomUUID();
            return;
        }
        input.value = '';
        this.fileInvalid = true;
    }

    public clearFile(input: HTMLInputElement): void {
        this.file = null;
        this.fileInvalid = false;
        this.error = '';
        input.value = '';
    }

    public async download(): Promise<void> {
        const attachment = this.form.attachment;
        if (!attachment || this.busy) { return; }
        this.busy = true;
        this.error = '';
        try {
            downloadBlob(await this.api.downloadMissing(attachment.id), attachment.name);
        } catch (error) {
            console.error('Attachment download failed', error);
            this.error = 'Could not download the attachment. Please retry.';
        } finally {
            this.busy = false;
        }
    }

    public ngOnInit(): void {
        const { key, index } = this.df.missingModal;
        this.index = index;
        if (index !== null && this.df.missing[index]) {
            const { key: _k, ...rest } = this.df.missing[index];
            this.form = { ...rest };
        } else if (key) {
            const [type, land, l2] = key.split('|') as [InputDocType, Landscape, string];
            this.form = { type, land, l2, title: '' };
        }
    }

    public get isEdit(): boolean {
        return this.index !== null;
    }

    public async save(): Promise<void> {
        if (this.busy) { return; }
        if (!this.form.title?.trim()) {
            this.titleInvalid = true;
            return;
        }
        if (this.fileInvalid) { return; }
        this.error = '';
        let value = { ...this.form, title: this.form.title.trim() };
        if (this.file || value.attachment) {
            this.busy = true;
            try {
                const id = this.form.id ?? this.uploadId;
                const document = { ...value, id, key: `${value.type}|${value.land}|${value.l2}` };
                value = this.file
                    ? await this.api.uploadMissing(document, this.file, id)
                    : await this.api.editUploadedMissing(document);
            } catch (error) {
                console.error('Missing document upload failed', error);
                this.error = error instanceof HttpErrorResponse
                    ? error.error?.error?.message ?? 'Upload failed. Please retry.'
                    : error instanceof Error ? error.message : 'Upload failed. Please retry.';
                return;
            } finally {
                this.busy = false;
            }
        }
        this.df.saveMissing(value, this.index);
        const edit = this.isEdit;
        this.df.closeAll();
        this.df.toast(value.attachment ? 'Uploaded; not yet searchable' : edit ? 'Changes saved' : 'Missing document reported');
    }

    public cancel(): void {
        if (this.busy) { return; }
        this.df.closeAll();
    }
}
